/**
 * Shared types for user interaction and tool confirmation system
 * Based on gemini-cli core types for consistency
 */

// Tool confirmation outcome enum - matches gemini-cli exactly
export enum ToolConfirmationOutcome {
  ProceedOnce = 'proceed_once',
  ProceedAlways = 'proceed_always', 
  ProceedAlwaysServer = 'proceed_always_server',
  ProceedAlwaysTool = 'proceed_always_tool',
  ModifyWithEditor = 'modify_with_editor',
  Cancel = 'cancel',
}

// Streaming and conversation state enums
export enum StreamingState {
  Idle = 'idle',
  Responding = 'responding',
  WaitingForConfirmation = 'waiting_for_confirmation',
}

export enum ConversationStatus {
  Active = 'active',
  Paused = 'paused',
  WaitingConfirmation = 'waiting_confirmation',
  Completed = 'completed',
}

// Tool confirmation detail interfaces
export interface BaseToolConfirmationDetails {
  type: 'edit' | 'exec' | 'info' | 'mcp';
  onConfirm: (outcome: ToolConfirmationOutcome) => void;
}

export interface ToolEditConfirmationDetails extends BaseToolConfirmationDetails {
  type: 'edit';
  fileName: string;
  fileDiff: string;
  isModifying?: boolean;
}

export interface ToolExecuteConfirmationDetails extends BaseToolConfirmationDetails {
  type: 'exec';
  command: string;
  rootCommand: string;
}

export interface ToolInfoConfirmationDetails extends BaseToolConfirmationDetails {
  type: 'info';
  prompt: string;
  urls?: string[];
}

export interface ToolMcpConfirmationDetails extends BaseToolConfirmationDetails {
  type: 'mcp';
  serverName: string;
  toolName: string;
}

export type ToolCallConfirmationDetails =
  | ToolEditConfirmationDetails
  | ToolExecuteConfirmationDetails
  | ToolMcpConfirmationDetails
  | ToolInfoConfirmationDetails;

// SSE Event types for agentic streaming
export interface ToolCallRequestEvent {
  messageId: string;
  toolCallId: string;
  toolName: string;
  parameters: Record<string, unknown>;
}

export interface ToolCallResponseEvent {
  messageId: string;
  toolCallId: string;
  result: unknown;
  success: boolean;
}

export interface ToolConfirmationRequiredEvent {
  messageId: string;
  toolCallId: string;
  confirmationDetails: ToolCallConfirmationDetails;
}

export interface ConversationStatusEvent {
  messageId: string;
  status: ConversationStatus;
  reason?: string;
  toolCallId?: string;
}

export interface ToolConfirmationResponseEvent {
  toolCallId: string;
  outcome: ToolConfirmationOutcome;
}

// SSE Event wrapper
export interface SSEEvent<T = unknown> {
  type: string;
  data: T;
}

// Tool execution status
export enum ToolExecutionStatus {
  Validating = 'validating',
  Scheduled = 'scheduled',
  AwaitingApproval = 'awaiting_approval',
  Executing = 'executing',
  Success = 'success',
  Error = 'error',
  Cancelled = 'cancelled',
}

export interface ToolCallState {
  id: string;
  toolName: string;
  status: ToolExecutionStatus;
  parameters?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  liveOutput?: string;
  confirmationDetails?: ToolCallConfirmationDetails;
  startTime?: number;
  duration?: number;
}

// Approval mode settings
export enum ApprovalMode {
  Default = 'default',
  AutoEdit = 'auto_edit', 
  YOLO = 'yolo',
}

export interface UserPreferences {
  approvalMode: ApprovalMode;
  alwaysApproveTools: Set<string>;
  alwaysApproveServers: Set<string>;
}